import { useTranslation } from '@dukanti/shared';
import type { StorefrontNewsletterSubscriberAdminRow } from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface NewsletterSubscriberSuppressDialogProps {
  readonly subscriber: StorefrontNewsletterSubscriberAdminRow | null;
  readonly isPending: boolean;
  readonly hasError: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

export function NewsletterSubscriberSuppressDialog({
  subscriber,
  isPending,
  hasError,
  onCancel,
  onConfirm,
}: NewsletterSubscriberSuppressDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  return (
    <AlertDialog
      open={subscriber !== null}
      onOpenChange={(open) => {
        if (!open && !isPending) onCancel();
      }}
    >
      <AlertDialogContent
        dir={dir}
        data-testid="newsletter-suppress-dialog"
        className="max-w-[calc(100vw-2rem)] sm:max-w-lg"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.newsletterSubscribers.suppressTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.newsletterSubscribers.suppressBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <p className="break-all text-start">
          <bdi dir="ltr">{subscriber?.contactValue}</bdi>
        </p>
        {hasError ? (
          <p role="alert" className="text-destructive text-sm">
            {t('admin.newsletterSubscribers.suppressError')}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t('common.cancel')}</AlertDialogCancel>
          <Button type="button" variant="destructive" disabled={isPending} onClick={onConfirm}>
            {t(isPending ? 'common.loading' : 'admin.newsletterSubscribers.suppress')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
